import { motion } from 'motion/react';
import { TrendingUp, Scale, Coffee, Target, Users, Lightbulb, ArrowRight, Briefcase, Globe, Rocket } from 'lucide-react';

interface ForEntrepreneursPathwayProps {
  onNavigateHome: () => void;
  onNavigateToBusinessConsulting: () => void;
  onNavigateToLegalSupport: () => void;
  onNavigateToBeverageDevelopment: () => void;
}

export function ForEntrepreneursPathway({
  onNavigateHome,
  onNavigateToBusinessConsulting,
  onNavigateToLegalSupport,
  onNavigateToBeverageDevelopment
}: ForEntrepreneursPathwayProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Section 1: Hero Header */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-white mb-6 text-[48px]">Support for Entrepreneurs in the U.S.</h1>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                Market entry strategy, operational support, and execution — tailored to your vision.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Free 15-min Consultation
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBlbnRyZXByZW5ldXJ8ZW58MXx8fHwxNzY1MTEzOTAyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Business meeting with entrepreneur"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: What We Help With */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">What We Help With</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for building and growing your business in the U.S.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: Briefcase,
                title: 'U.S. business formation and roadmap support (non-legal)',
                description: 'Navigate entity formation, EIN application, and business setup coordination'
              },
              {
                icon: Target,
                title: 'Market positioning & operational planning',
                description: 'Strategic planning for U.S. market entry and competitive positioning'
              },
              {
                icon: Users,
                title: 'Coordinating vendors and professional partners',
                description: 'Connect with co-packers, agencies, attorneys, and service providers'
              },
              {
                icon: Coffee,
                title: 'Beverage product development (if relevant)',
                description: 'Formula development, sourcing, packaging, and launch support'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-[#C6A667]/10 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-[#C6A667]" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-[#0A1A33] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Recommended Service Bundle */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Recommended Service Bundle</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Essential services for entrepreneurs entering the U.S. market
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Business Consulting',
                description: 'Strategic planning, market entry guidance, and operational support for your venture.',
                icon: TrendingUp,
                onClick: onNavigateToBusinessConsulting
              },
              {
                title: 'Legal Support Coordination',
                description: 'Coordinate with attorneys, prepare documents, and manage legal processes (non-legal advice).',
                icon: Scale,
                onClick: onNavigateToLegalSupport
              },
              {
                title: 'Beverage Development Consulting',
                description: 'End-to-end support for beverage concepts from formula to market launch.',
                icon: Coffee,
                onClick: onNavigateToBeverageDevelopment
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ y: -5 }}
                  className="group bg-gray-50 rounded-xl p-8 border-2 border-transparent hover:border-[#C6A667] transition-all duration-300 cursor-pointer"
                  onClick={service.onClick}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <button 
                    className="group/btn inline-flex items-center text-[#C6A667] hover:text-[#D4B578] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      service.onClick();
                    }}
                  >
                    <span>Explore Service</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Example Founder Journeys */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Founder Success Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real scenarios from entrepreneurs we&apos;ve supported
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Globe,
                title: 'Starting a company while overseas',
                description: 'A founder in China wanted to establish a U.S. entity. We coordinated formation, EIN application, and connected them with local legal and accounting partners.'
              },
              {
                icon: Coffee,
                title: 'Launching a beverage concept in the U.S.',
                description: 'An entrepreneur had a tea concept. We guided formula development, found co-packers, designed packaging, and coordinated the product launch.'
              },
              {
                icon: Rocket,
                title: 'Coordinating with lawyers, suppliers, and agencies',
                description: 'A growing business needed vendor coordination. We managed communications with their attorney, suppliers, and marketing agency while they focused on growth.'
              }
            ].map((scenario, index) => {
              const IconComponent = scenario.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 rounded-xl shadow-lg"
                >
                  <div className="w-14 h-14 bg-[#C6A667]/10 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">{scenario.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{scenario.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A streamlined process to accelerate your business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                number: '01',
                title: 'Free Consultation',
                description: 'Share your business vision in a 15-minute call'
              },
              {
                number: '02',
                title: 'Custom Proposal',
                description: 'Receive a strategic roadmap and clear pricing'
              },
              {
                number: '03',
                title: 'We Execute',
                description: 'We coordinate vendors, handle logistics, and drive progress'
              },
              {
                number: '04',
                title: 'You Grow',
                description: 'Focus on strategy while we manage execution'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-[#C6A667]/20 mb-4 text-[64px] leading-none">{step.number}</div>
                <h3 className="text-[#0A1A33] mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 -right-4 w-8 h-px bg-[#C6A667]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Social Proof */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-8 text-[36px]">Trusted by Founders</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                metric: '50+',
                label: 'Businesses Supported'
              },
              {
                metric: '10+',
                label: 'Product Launches'
              },
              {
                metric: '100%',
                label: 'Client Satisfaction'
              }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="text-[#C6A667] text-[48px] mb-2">{stat.metric}</div>
                <div className="text-[#0A1A33]/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white p-10 rounded-xl shadow-lg max-w-3xl mx-auto text-center"
          >
            <Lightbulb className="w-12 h-12 text-[#C6A667] mx-auto mb-6" />
            <p className="text-gray-700 text-lg leading-relaxed italic mb-4">
              &quot;Strategic guidance with hands-on execution. They understand what founders need.&quot;
            </p>
            <div className="text-sm text-[#0A1A33]">— Beverage Entrepreneur, Los Angeles</div>
          </motion.div>
        </div>
      </section>

      {/* Section 7: Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-white mb-8 text-[40px]">
              Let&apos;s build something great — together.
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="group px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-3"
            >
              <span>Book Free 15-min Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
